import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "react-hot-toast";
import PreviewModal from "./PreviewModal";
import SelectPeriodModal from "./SelectPeriodModal";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../components/CustomButton";

const AutomatedDocuments = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [openPeriodModal, setOpenPeriodModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);

  const getDocumentType = () =>
    activeTab === 0 ? "employment_certificate" : "payslip";

  // Handle preview employment certificate
  const handlePreviewEmploymentCertificate = async () => {
    setPreviewing(true);
    try {
      const res = await fetch(`${BASE_URL}/documents/worker/preview/employment-certificate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${decryptedToken}`,
        },
      });

      const data = await res.json();
      if (!data.error && data.data) {
        setPreviewData(data.data);
        setOpenPreview(true);
      } else {
        toast.error(data.message || "Failed to fetch employment certificate preview.");
      }
    } catch (err) {
      console.error("Error fetching employment certificate:", err);
      toast.error("Something went wrong while fetching document preview.");
    } finally {
      setPreviewing(false);
    }
  };

  // Handle preview payslip
  const handlePreviewPayslip = async (period) => {
    setPreviewing(true);
    try {
      const res = await fetch(`${BASE_URL}/documents/worker/preview/payslip`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${decryptedToken}`,
        },
        body: JSON.stringify({ period }),
      });

      const data = await res.json();
      console.log("data payslip", data);
      console.log("Selected period for API:", period);
      if (!data.error && data.data) {
        console.log("Setting preview data with period:", data.data.period);
        setPreviewData(data.data);
        setOpenPreview(true);
        setSelectedPeriod(null); // reset period after use
      } else {
        toast.error(data.message || "Failed to fetch payslip preview.");
      }
    } catch (err) {
      console.error("Error fetching payslip:", err);
      toast.error("Something went wrong while fetching document preview.");
    } finally {
      setPreviewing(false);
    }
  };


  // Handle period selection for payslip
  const handlePeriodConfirm = (period) => {
    console.log("📋 handlePeriodConfirm called with period:", period);
    setSelectedPeriod(period);
    setOpenPeriodModal(false);
    // Small delay to ensure modal closes before opening preview
    setTimeout(() => {
      // Preview payslip with selected period
      handlePreviewPayslip(period);
    }, 100);
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: 2,
          border: "2px solid #091E4224",
          py: 1,
          px: 0,
          mx: 2,
        }}
      >
        {/* Header Row */}


        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(e, v) => {
            setActiveTab(v);
          }}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            mt: 0.5,
            "& .MuiTab-root": {
              fontFamily: "Poppins, sans-serif",
              fontSize: "15px",
              fontWeight: 500,
              textTransform: "none",
              color: "#44546F",
            },
          }}
        >
          <Tab label={t("automatedDocuments.tabs.employment_certificates")} />
          <Tab label={t("automatedDocuments.tabs.payslips")} />
        </Tabs>

        <Box sx={{ mt: 3, px: 2, pb: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "400px",
              gap: 3,
            }}
          >
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                fontSize: "20px",
                color: "#003149",
                textAlign: "center",
              }}
            >
              {activeTab === 0
                ? t("automatedDocuments.preview.employmentCertificateTitle") || "Employment Certificate"
                : t("automatedDocuments.preview.payslipTitle") || "Payslip"}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                color: "#6B7280",
                textAlign: "center",
                maxWidth: "500px",
              }}
            >
              {activeTab === 0
                ? t("automatedDocuments.preview.employmentCertificateDescription") ||
                "Preview and generate your employment certificate document."
                : t("automatedDocuments.preview.payslipDescription") ||
                "Select a period to preview and generate your payslip."}
            </Typography>

            <CustomButton
              label={
                previewing ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                    {t("automatedDocuments.preview.loading") || "Loading..."}
                  </Box>
                ) : activeTab === 0 ? (
                  t("automatedDocuments.preview.previewCertificate") || "Preview Employment Certificate"
                ) : (
                  t("automatedDocuments.preview.selectPeriod") || "Select Period & Preview Payslip"
                )
              }
              onClick={() => {
                if (activeTab === 0) {
                  handlePreviewEmploymentCertificate();
                } else {
                  setOpenPeriodModal(true);
                }
              }}
              disabled={previewing}
              size="medium"
              sx={{ minWidth: "250px" }}
            />
          </Box>
        </Box>

      </Box>
      <PreviewModal
        key={previewData?.preview_data?.period || previewData?.document_type || 'preview'} // Force re-render when period changes
        open={openPreview}
        onClose={() => {
          setOpenPreview(false);
          setPreviewData(null);
        }}
        data={previewData}
      />
      <SelectPeriodModal
        open={openPeriodModal}
        onClose={() => setOpenPeriodModal(false)}
        onConfirm={handlePeriodConfirm}
      />
    </>
  );
};

export default AutomatedDocuments;
